<template>
    <div
        class="modal-container"
        :class="[scroll && `scroll_${scroll}`]"
        @click="handleOutsideEvent">
        <FocusTrap :escapeDeactivates="false" :fallbackFocus="fallbackFocusSelector">
            <dialog
                ref="dialogRef"
                @click.stop
                class="modal-box"
                tabindex="-1"
                :class="[
                    onClose && 'closeable',
                    size && `size_${size}`,
                    height && `height_${height}`,
                    minHeight && `min_height_${minHeight}`,
                    animation && `animation_${animation}`,
                    position && `position_${position}`,
                    border && `border_${border}`,
                ]">
                <div
                    class="modal-box-content"
                    :class="[padding !== 'none' && `padding_${padding}`]">
                    <Header
                        v-if="!warning && !slots.content"
                        :onClose="onClose && handleClose"
                        :onBack="onBack && handleBack"
                        :title="title"
                        :subTitle="subTitle"
                        :disabled="disableHeader" />

                    <Warning
                        v-if="warning"
                        :onAccept="handleWarningAccept"
                        :onDeny="handleWarningDeny" />
                    <div v-else class="modal-box-details">
                        <slot v-if="slots.content" name="content" />
                        <slot v-else />
                    </div>
                </div>

                <Errors v-if="!warning" :errors="errors" />
                <div
                    v-if="!warning && !slots.content && slots.footer"
                    class="modal-box-footer"
                    :class="[padding !== 'none' && `padding_${padding}`]">
                    <slot name="footer" />
                </div>
            </dialog>
        </FocusTrap>
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import {
        onMounted,
        onBeforeUnmount,
        useSlots,
        ref,
        defineComponent,
        nextTick,
        PropType,
    } from 'vue';
    import {
        Animation,
        Padding,
        Border,
        Size,
        Height,
        Position,
        Scroll,
        MinHeight,
    } from './Modal.interfaces';
    import { FocusTrap } from 'focus-trap-vue';
    import { useDialogManager } from '@shared/helpers/dialogManager';
    import capitalize from 'lodash/capitalize';
    import Warning from './Warning/Warning.vue';
    import Header from './Header/Header.vue';
    import Errors from './Errors/Errors.vue';

    export default defineComponent({
        name: 'Modal',
        components: { FocusTrap, Warning, Header, Errors },
        methods: { t },
        props: {
            title: String,
            subTitle: {
                type: String as PropType<string | null>,
            },
            disableHeader: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            lockBody: {
                type: Boolean,
                default: true,
            },
            onBack: Function,
            onOpen: Function,
            onClose: Function,
            onCloseOutside: {
                type: Boolean,
                default: true,
            },
            onCloseWarning: {
                type: Boolean,
                default: false,
            },
            position: {
                type: String as PropType<Position>,
                default: 'center',
            },
            size: {
                type: String as PropType<Size>,
                default: 'm',
            },
            height: {
                type: String as PropType<Height>,
                default: 'auto',
            },
            minHeight: {
                type: String as PropType<MinHeight>,
            },
            padding: {
                type: String as PropType<Padding>,
                default: 'm',
            },
            border: {
                type: String as PropType<Border>,
                default: 'radius',
            },
            scroll: {
                type: String as PropType<Scroll>,
                default: 'body',
            },
            animation: {
                type: String as PropType<Animation>,
                default: 'fade-in-bottom',
            },
            errors: {
                type: Array as PropType<string[]>,
                default: () => [],
            },
        },
        setup(props) {
            const slots = useSlots();
            const dialogManager = useDialogManager();
            const dialogRef = ref<HTMLDialogElement>();

            const warning = ref<boolean>(false);

            const fallbackFocusSelector = () => dialogRef.value || document.body;

            onMounted(() => {
                dialogManager.register({
                    lockBody: props.lockBody,
                    onEscClose: handleOutsideEvent,
                });

                nextTick(handleOpen);
            });

            onBeforeUnmount(() => {
                dialogManager.unregister();
            });

            const handleOpen = () => {
                props.onOpen && props.onOpen();
            };

            const handleOutsideEvent = () => {
                props.onCloseOutside && handleClose();
            };

            const setWarning = (state: boolean) => {
                warning.value = state;
            };

            const handleWarningDeny = () => {
                setWarning(false);
            };

            const handleWarningAccept = () => {
                props.onClose && props.onClose();
            };

            const handleClose = () => {
                if (props.onClose) {
                    if (props.onCloseWarning) {
                        setWarning(true);
                    } else {
                        props.onClose();
                    }
                }
            };

            const handleBack = () => {
                props.onBack && props.onBack();
            };

            return {
                slots,
                dialogRef,
                fallbackFocusSelector,
                warning,
                capitalize,
                handleClose,
                handleBack,
                handleWarningDeny,
                handleWarningAccept,
                handleOutsideEvent,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './Modal.scss';
</style>
